import { type ComponentProps, useId } from 'react';
import { clsx } from 'clsx';
import { type FieldValues, useController, type UseControllerProps } from 'react-hook-form';

import { Field, FieldError, FieldLabel } from '@/components/ui/field.tsx';
import { cn } from '@/lib/utils';

type FormSelectProps<T extends FieldValues> = {
  label: string;
  labelHidden?: boolean;
  options: { value: string; label: string }[];
} & UseControllerProps<T> &
  Omit<ComponentProps<'select'>, 'value' | 'onChange'>;

const FormSelect = <T extends FieldValues>({
  label,
  labelHidden,
  options,
  className,
  ...props
}: FormSelectProps<T>) => {
  const id = useId();

  const {
    field: { ref, value, ...field },
    fieldState: { error },
  } = useController(props);

  return (
    <Field className={className} data-invalid={!!error?.message}>
      <FieldLabel htmlFor={id} className={clsx(labelHidden && 'sr-only')}>
        {label}
      </FieldLabel>
      <select
        {...props}
        {...field}
        id={id}
        value={value ?? ''}
        ref={ref}
        aria-invalid={!!error?.message}
        className={cn(
          'bg-input/20 dark:bg-input/30 border-input focus-visible:border-ring focus-visible:ring-ring/30 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 h-7 rounded-md border px-2 py-0.5 text-sm transition-colors focus-visible:ring-[2px] aria-invalid:ring-[2px] md:text-xs/relaxed w-full min-w-0 outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <FieldError>{error?.message}</FieldError>
    </Field>
  );
};

export default FormSelect;
